import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
const pageVariants = {
  initial: {
    opacity: 0,
    y: 20
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.4,
      ease: 'easeIn'
    }
  }
};
export function Contact() {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      setFormState({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      setTimeout(() => setIsSubmitted(false), 5000);
    }, 1500);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormState({
      ...formState,
      [e.target.name]: e.target.value
    });
  };
  return <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" className="w-full min-h-screen py-24 bg-cafe-50" data-id="element-191">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-192">
        <div className="text-center mb-16" data-id="element-193">
          <h1 className="font-serif text-5xl font-bold text-cafe-900 mb-6" data-id="element-194">
            Get in Touch
          </h1>
          <p className="text-cafe-800/70 max-w-2xl mx-auto" data-id="element-195">
            Have a question, feedback, or want to inquire about catering? We'd
            love to hear from you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-196">
          {/* Contact Info & Map */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="space-y-12" data-id="element-197">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-198">
              <div className="flex gap-4" data-id="element-199">
                <div className="w-12 h-12 rounded-full bg-cafe-sage/20 flex items-center justify-center shrink-0" data-id="element-200">
                  <MapPin className="w-6 h-6 text-cafe-sage" data-id="element-201" />
                </div>
                <div data-id="element-202">
                  <h3 className="font-serif text-xl font-bold text-cafe-900 mb-2" data-id="element-203">
                    Location
                  </h3>
                  <p className="text-cafe-800/80 text-sm leading-relaxed" data-id="element-204">
                    123 Artisan Avenue
                    <br data-id="element-205" />
                    Coffee District, CD 10012
                  </p>
                </div>
              </div>

              <div className="flex gap-4" data-id="element-206">
                <div className="w-12 h-12 rounded-full bg-cafe-sage/20 flex items-center justify-center shrink-0" data-id="element-207">
                  <Clock className="w-6 h-6 text-cafe-sage" data-id="element-208" />
                </div>
                <div data-id="element-209">
                  <h3 className="font-serif text-xl font-bold text-cafe-900 mb-2" data-id="element-210">
                    Hours
                  </h3>
                  <p className="text-cafe-800/80 text-sm leading-relaxed" data-id="element-211">
                    Mon-Fri: 7am - 6pm
                    <br data-id="element-212" />
                    Sat-Sun: 8am - 7pm
                  </p>
                </div>
              </div>

              <div className="flex gap-4" data-id="element-213">
                <div className="w-12 h-12 rounded-full bg-cafe-sage/20 flex items-center justify-center shrink-0" data-id="element-214">
                  <Phone className="w-6 h-6 text-cafe-sage" data-id="element-215" />
                </div>
                <div data-id="element-216">
                  <h3 className="font-serif text-xl font-bold text-cafe-900 mb-2" data-id="element-217">
                    Phone
                  </h3>
                  <p className="text-cafe-800/80 text-sm leading-relaxed" data-id="element-218">
                    (555) 123-4567
                  </p>
                </div>
              </div>

              <div className="flex gap-4" data-id="element-219">
                <div className="w-12 h-12 rounded-full bg-cafe-sage/20 flex items-center justify-center shrink-0" data-id="element-220">
                  <Mail className="w-6 h-6 text-cafe-sage" data-id="element-221" />
                </div>
                <div data-id="element-222">
                  <h3 className="font-serif text-xl font-bold text-cafe-900 mb-2" data-id="element-223">
                    Email
                  </h3>
                  <p className="text-cafe-800/80 text-sm leading-relaxed" data-id="element-224">
                    hello@brewandbloom.com
                  </p>
                </div>
              </div>
            </div>

            {/* Map Placeholder */}
            <div className="w-full h-64 bg-cafe-800/10 rounded-lg overflow-hidden relative group" data-id="element-225">
              <div className="absolute inset-0 flex items-center justify-center flex-col gap-2 text-cafe-800/50 group-hover:text-cafe-sage transition-colors" data-id="element-226">
                <MapPin className="w-8 h-8" data-id="element-227" />
                <span className="font-medium" data-id="element-228">Interactive Map Placeholder</span>
              </div>
              <img src="https://images.unsplash.com/photo-1569336415962-a4bd9f69cd83?auto=format&fit=crop&q=80&w=1000" alt="Map area" className="w-full h-full object-cover opacity-30 mix-blend-multiply" data-id="element-229" />
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="bg-white p-8 md:p-10 rounded-xl shadow-sm border border-cafe-800/5" data-id="element-230">
            <h2 className="font-serif text-3xl font-bold text-cafe-900 mb-8" data-id="element-231">
              Send a Message
            </h2>

            {isSubmitted ? <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="bg-cafe-sage/10 text-cafe-sage p-6 rounded-lg text-center border border-cafe-sage/20" data-id="element-232">
                <div className="w-16 h-16 bg-cafe-sage text-white rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-233">
                  <Send className="w-8 h-8" data-id="element-234" />
                </div>
                <h3 className="font-serif text-2xl font-bold mb-2" data-id="element-235">
                  Message Sent!
                </h3>
                <p data-id="element-236">
                  Thank you for reaching out. We'll get back to you shortly.
                </p>
              </motion.div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-237">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-238">
                  <div className="space-y-2" data-id="element-239">
                    <label htmlFor="name" className="text-sm font-medium text-cafe-900" data-id="element-240">
                      Name
                    </label>
                    <input type="text" id="name" name="name" required value={formState.name} onChange={handleChange} className="w-full px-4 py-3 bg-cafe-50 border border-cafe-800/10 rounded-md focus:outline-none focus:ring-2 focus:ring-cafe-sage focus:border-transparent transition-all" placeholder="Jane Doe" data-id="element-241" />
                  </div>
                  <div className="space-y-2" data-id="element-242">
                    <label htmlFor="email" className="text-sm font-medium text-cafe-900" data-id="element-243">
                      Email
                    </label>
                    <input type="email" id="email" name="email" required value={formState.email} onChange={handleChange} className="w-full px-4 py-3 bg-cafe-50 border border-cafe-800/10 rounded-md focus:outline-none focus:ring-2 focus:ring-cafe-sage focus:border-transparent transition-all" placeholder="jane@example.com" data-id="element-244" />
                  </div>
                </div>

                <div className="space-y-2" data-id="element-245">
                  <label htmlFor="subject" className="text-sm font-medium text-cafe-900" data-id="element-246">
                    Subject
                  </label>
                  <input type="text" id="subject" name="subject" required value={formState.subject} onChange={handleChange} className="w-full px-4 py-3 bg-cafe-50 border border-cafe-800/10 rounded-md focus:outline-none focus:ring-2 focus:ring-cafe-sage focus:border-transparent transition-all" placeholder="How can we help?" data-id="element-247" />
                </div>

                <div className="space-y-2" data-id="element-248">
                  <label htmlFor="message" className="text-sm font-medium text-cafe-900" data-id="element-249">
                    Message
                  </label>
                  <textarea id="message" name="message" required rows={5} value={formState.message} onChange={handleChange} className="w-full px-4 py-3 bg-cafe-50 border border-cafe-800/10 rounded-md focus:outline-none focus:ring-2 focus:ring-cafe-sage focus:border-transparent transition-all resize-none" placeholder="Write your message here..." data-id="element-250"></textarea>
                </div>

                <button type="submit" disabled={isSubmitting} className="w-full bg-cafe-900 text-white py-4 rounded-md font-medium hover:bg-cafe-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-251">
                  {isSubmitting ? <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full" data-id="element-252" /> : <>
                      Send Message
                      <Send className="w-4 h-4" data-id="element-253" />
                    </>}
                </button>
              </form>}
          </motion.div>
        </div>
      </div>
    </motion.div>;
}